import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CategoryShowcaseProvider } from '../context/CategoryShowcaseContext';
import { useCategoryShowcase } from '../context/useCategoryShowcase';
import CategoryCard from './CategoryCard';
import '../styles/categoryShowcase.css';

const AUTO_SCROLL_MS = 3000;
const SKELETON_COUNT = 5;

const PrevIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 18l-6-6 6-6" />
    </svg>
);

const NextIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18l6-6-6-6" />
    </svg>
);

const ChevronRight: React.FC = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
);

interface InnerProps {
    compact?: boolean;
}

const CategoryShowcaseInner: React.FC<InnerProps> = ({ compact }) => {
    const { state, fetchPublic } = useCategoryShowcase();
    const { categories, loading } = state;

    const trackRef  = useRef<HTMLDivElement>(null);
    const timerRef  = useRef<ReturnType<typeof setInterval> | null>(null);
    const [canPrev, setCanPrev]   = useState(false);
    const [canNext, setCanNext]   = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [activeDot, setActiveDot] = useState(0);

    useEffect(() => { fetchPublic(); }, [fetchPublic]);

    // ── scroll helpers ────────────────────────────────────────────────────
    const cardWidth = useCallback(() => {
        const el = trackRef.current;
        if (!el) return 240;
        const card = el.querySelector('.cs-card') as HTMLElement | null;
        return card ? card.offsetWidth + 20 : 240; // card + gap
    }, []);

    const syncState = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        setCanPrev(el.scrollLeft > 4);
        setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
        const w = cardWidth();
        setActiveDot(Math.round(el.scrollLeft / w));
    }, [cardWidth]);

    const scrollBy = useCallback((dir: 'prev' | 'next') => {
        const el = trackRef.current;
        if (!el) return;
        el.scrollBy({ left: dir === 'next' ? cardWidth() : -cardWidth(), behavior: 'smooth' });
        setTimeout(syncState, 350);
    }, [cardWidth, syncState]);

    // ── auto-scroll ────────────────────────────────────────────────────────
    useEffect(() => {
        if (loading || categories.length === 0) return;

        const tick = () => {
            const el = trackRef.current;
            if (!el || isPaused) return;
            const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
            if (atEnd) {
                el.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
                el.scrollBy({ left: cardWidth(), behavior: 'smooth' });
            }
            setTimeout(syncState, 350);
        };

        timerRef.current = setInterval(tick, AUTO_SCROLL_MS);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [loading, categories.length, isPaused, cardWidth, syncState]);

    // ── dot count ──────────────────────────────────────────────────────────
    const dotCount = categories.length;

    return (
        <section className={`cs-section${compact ? ' cs-section--compact' : ''}`}>
            <div className="container">

                {/* Header */}
                <div className="cs-header">
                    <span className="cs-eyebrow">COLLECTIONS</span>
                    <h2 className="cs-title">Shop by Category</h2>
                    <p className="cs-subtitle">
                        Explore our handcrafted collections — from vibrant pom poms to elegant wall decor
                    </p>
                </div>

                {/* Carousel */}
                <div
                    className="cs-carousel"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    {/* Prev button */}
                    <button
                        className={`cs-nav-btn cs-nav-btn--prev${!canPrev ? ' cs-nav-btn--hidden' : ''}`}
                        onClick={() => scrollBy('prev')}
                        aria-label="Previous categories"
                    >
                        <PrevIcon />
                    </button>

                    {/* Track */}
                    <div
                        ref={trackRef}
                        className="cs-carousel-track"
                        onScroll={syncState}
                    >
                        {loading
                            ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                                <div key={i} className="cs-skeleton-card cs-skeleton-card--fixed">
                                    <div className="cs-skeleton-img" />
                                    <div className="cs-skeleton-body">
                                        <div className="cs-skeleton-text" />
                                        <div className="cs-skeleton-circle" />
                                    </div>
                                </div>
                            ))
                            : categories.map((cat, i) => (
                                <CategoryCard key={cat._id} category={cat} index={i} />
                            ))
                        }
                    </div>

                    {/* Next button */}
                    <button
                        className={`cs-nav-btn cs-nav-btn--next${!canNext ? ' cs-nav-btn--hidden' : ''}`}
                        onClick={() => scrollBy('next')}
                        aria-label="Next categories"
                    >
                        <NextIcon />
                    </button>
                </div>

                {/* Dot indicators */}
                {!loading && dotCount > 1 && (
                    <div className="cs-dots">
                        {Array.from({ length: dotCount }).map((_, i) => (
                            <button
                                key={i}
                                className={`cs-dot${i === activeDot ? ' cs-dot--active' : ''}`}
                                aria-label={`Go to category ${i + 1}`}
                                onClick={() => {
                                    const el = trackRef.current;
                                    if (!el) return;
                                    el.scrollTo({ left: i * cardWidth(), behavior: 'smooth' });
                                    setTimeout(syncState, 350);
                                }}
                            />
                        ))}
                    </div>
                )}

                {/* View all link */}
                {!loading && categories.length > 0 && (
                    <div className="cs-view-all">
                        <Link to="/categories" className="cs-view-all-link">
                            Browse all categories <ChevronRight />
                        </Link>
                    </div>
                )}

            </div>
        </section>
    );
};

export const CategoryShowcaseSection: React.FC<{ compact?: boolean }> = ({ compact }) => (
    <CategoryShowcaseProvider>
        <CategoryShowcaseInner compact={compact} />
    </CategoryShowcaseProvider>
);
